import { FormConfig, FormValues, NextFunction } from '@topdanmark-onlinesolutions/claim-form-types';
import { getValueOrDefault } from '../components/values/Get';
import { isCustomerClaimCovered } from '../helpers/business-rules/isCustomerClaimCovered';
import {
    getClaimDetails,
    getWhatHasBeenDamaged,
    getWorkshopDetails,
    isCustomerWithExistingVehiclePolicy
} from './shared/vehicle';
import { isCustomerAllowedToSelectWorkshop } from '../helpers/business-rules/isCustomerAllowedToSelectWorkshop';

export const getNextPage: NextFunction = async (curPageId: string, formConfig: FormConfig, formValues: FormValues) => {
    let nextPage: string | undefined = '';

    switch (curPageId) {
        case 'godt-at-vide':
            return 'dato';
        case 'dato':
            nextPage = 'tidspunkt';
            break;
        case 'tidspunkt':
            nextPage = 'adresse';
            break;
        case 'adresse':
            nextPage = 'beskriv-skaden';
            break;
        case 'beskriv-skaden':
            nextPage = 'vaelg-koeretoej';
            break;

        case 'vaelg-koeretoej':
            const registrationNumberField = getValueOrDefault(formConfig, formValues, 'registrationNumber');
            nextPage = registrationNumberField.registrationNumber === '' ? 'andet-koeretoej-nummerplade' : 'ansvar';
            break;

        case 'andet-koeretoej-nummerplade':
            nextPage = 'om-andet-koeretoej';
            break;

        case 'om-andet-koeretoej':
            nextPage = 'ansvar';
            break;

        case 'ansvar':
            nextPage = 'hvad-er-skadet';
            break;
        case 'hvad-er-skadet':
            nextPage = getNextDamagePage(curPageId, formConfig, formValues);
            break;
        case 'personskade':
            nextPage = getNextDamagePage(curPageId, formConfig, formValues);
            break;
        case 'oplysninger-om-passagerer':
            nextPage = getNextDamagePage(curPageId, formConfig, formValues);
            break;
        case 'skadezoner':
            nextPage = getNextDamagePage(curPageId, formConfig, formValues);
            break;
        case 'skader-andet-koeretoej':
            nextPage = getNextDamagePage(curPageId, formConfig, formValues);
            break;
        case 'skade-ting':
            if (getValueOrDefault(formConfig, formValues, 'areYouReadyToRegister').value === 'yes') {
                nextPage = 'genstandsregistrering';
            } else {
                nextPage = 'oplysninger-ejeren';
            }
            break;
        case 'genstandsregistrering':
            nextPage = 'oplysninger-ejeren';
            break;

        case 'oplysninger-ejeren':
            const havingInfoAboutOthers = getValueOrDefault(
                formConfig,
                formValues,
                'doYouHaveInformationAboutOthersInvolved'
            );
            nextPage = havingInfoAboutOthers.value === 'yes' ? 'om-ejeren' : 'foerer';
            break;
        case 'om-ejeren':
            nextPage = 'foerer';
            break;
        case 'foerer':
            const driver = getValueOrDefault(formConfig, formValues, 'whoDroveYourVehicle');
            nextPage =
                driver.value === 'iDroveTheVehicle' || driver.value === 'noDriver'
                    ? 'paavirkning'
                    : 'foerer-oplysninger';
            break;
        case 'foerer-oplysninger':
            nextPage = 'paavirkning';
            break;
        case 'paavirkning':
            nextPage = 'dokumentation';
            break;
        case 'dokumentation':
            nextPage = 'politi-og-samtykke';
            break;
        case 'politi-og-samtykke':
            if (getValueOrDefault(formConfig, formValues, 'policeContacted').value === 'Y') {
                nextPage = 'samtykke';
            } else if (
                isCustomerClaimCovered(
                    getClaimDetails(formConfig, formValues),
                    getWorkshopDetails(formConfig, formValues)
                )
            ) {
                nextPage = 'daekket';
            } else if (isCustomerAllowedToSelectWorkshop(getWorkshopDetails(formConfig, formValues), 'cpr')) {
                nextPage = 'hvilken-skade';
            } else {
                nextPage = 'om-dig';
            }
            break;

        case 'samtykke':
            if (
                isCustomerClaimCovered(
                    getClaimDetails(formConfig, formValues),
                    getWorkshopDetails(formConfig, formValues)
                )
            ) {
                nextPage = 'daekket';
            } else if (isCustomerAllowedToSelectWorkshop(getWorkshopDetails(formConfig, formValues), 'cpr')) {
                nextPage = 'hvilken-skade';
            } else {
                nextPage = 'om-dig';
            }
            break;

        case 'hvilken-skade':
            nextPage = 'find-vaerksted';
            break;

        case 'daekket':
            if (isCustomerAllowedToSelectWorkshop(getWorkshopDetails(formConfig, formValues), 'cpr')) {
                nextPage = 'hvilken-skade';
            } else {
                nextPage = 'om-dig';
            }
            break;

        case 'find-vaerksted':
            nextPage = 'om-dig';
            break;
        case 'om-dig':
            nextPage = 'resume';
            break;
        case 'resume':
            nextPage = undefined;
            break;
        default:
            throw new Error('Unknown page: ' + curPageId);
    }

    return nextPage;
};

/**
 * Decides which damage page that are the next to show, depending on where in the form flow the user is, and which damage checkboxes that have been selected.
 * @param currentPageId The id of the current page
 * @param formConfig
 * @param formValues
 * @returns Next damage page to show.
 */
const getNextDamagePage = (
    currentPageId: string,
    formConfig: FormConfig,
    formValues: FormValues
): string | undefined => {
    const damageSelections = getWhatHasBeenDamaged(formConfig, formValues);

    switch (currentPageId) {
        case 'hvad-er-skadet':
            if (damageSelections.vehicle.isMyVehicleDamaged) {
                // The 'other' vehicle type can be selected in one of two ways:
                // 1. For a customer with a policy:
                //    Customer first selects 'Other' in the customerVehicle component, followed up by selecting 'Other' a second time in the radiobuttons list on the next page.
                //    Because the option selected in the radiobuttons list is not reset if user navigates back in the flow, we also need to check the registrationNumber value.
                // 2. For a customer without a policy (non policy case):
                //    The customerVehicle component will render vehicle options where 'Other' is one of the options.
                const isVehicleTypeOtherSelected = isCustomerWithExistingVehiclePolicy(formConfig, formValues)
                    ? getValueOrDefault(formConfig, formValues, 'registrationNumber').registrationNumber === '' &&
                      getValueOrDefault(formConfig, formValues, 'vehicleTypeOther').value === 'other'
                    : getValueOrDefault(formConfig, formValues, 'registrationNumber').entityType === 'others';

                // If a customer has selected 'other' as vehicle type -> skip the skadezoner page
                if (isVehicleTypeOtherSelected) {
                    return 'skader-andet-koeretoej';
                }

                return 'skadezoner';
            }
            if (damageSelections.persons.isDriverDamaged) return 'personskade';
            if (damageSelections.persons.isPassangersDamaged) return 'oplysninger-om-passagerer';
            if (damageSelections.things.isMyThingsDamaged) return 'skade-ting';

            return 'oplysninger-ejeren';
        case 'skader-andet-koeretoej':
        case 'skadezoner':
            if (damageSelections.persons.isDriverDamaged) return 'personskade';
            if (damageSelections.persons.isPassangersDamaged) return 'oplysninger-om-passagerer';
            if (damageSelections.things.isMyThingsDamaged) return 'skade-ting';

            return 'oplysninger-ejeren';
        case 'personskade':
            if (damageSelections.persons.isPassangersDamaged) return 'oplysninger-om-passagerer';
            if (damageSelections.things.isMyThingsDamaged) return 'skade-ting';

            return 'oplysninger-ejeren';
        case 'oplysninger-om-passagerer':
            if (damageSelections.things.isMyThingsDamaged) return 'skade-ting';

            return 'oplysninger-ejeren';
        case 'skade-ting':
            const isReadyToFileAllThingsDamaged =
                getValueOrDefault(formConfig, formValues, 'areYouReadyToRegister').value === 'yes';

            if (isReadyToFileAllThingsDamaged) return 'genstandsregistrering';

            return 'oplysninger-ejeren';
        case 'genstandsregistrering':
            return 'oplysninger-ejeren';
    }
};
